import React, { useContext, useMemo, useState } from 'react'
import { BASE_CATEGORIES, DEFAULT_SUBMODEL, NO_CATEGORIES_AVAILABLE_MESSAGE, NO_CATEGORY_FOUND_SEARCH_MESSAGE, SELECT_CATEGORIES, SELECTED_CATEGORIES, SHOW_ONLY_OVERRIDDEN, SHOW_ONLY_OVERRIDDEN_MESSAGE, VIEW_LESS, VIEW_MORE } from '../ARSConstants'
import { ArsContext } from '../Contexts/ArsContext'
import { SearchIcon, PlainDownArrow, UpArrow } from 'assests/GmsIcons'
import CategoryItem from './CategoryItem';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import { RecommenderLogsResponse, TSubmodel } from '../Types';

type BaseCategorySelectorProps = {
    submodel: TSubmodel
}

const BaseCategorySelector = ({ submodel }: BaseCategorySelectorProps) => {
    const [searchString, setSearchString] = useState<string>("");
    const [selectedCategoryTypeId, setSelectedCategoryTypeId] = useState<string>("");
    const [viewMoreCategories, setViewMoreCategories] = useState<boolean>(false);
    const [showOnlyOverridden, setShowOnlyOverridden] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(true);
    const { model, setModel, allCategories, allCategoryTypes, numberOfCategoriesToShow } = useContext(ArsContext);

    const hasDefaultSubmodel = (model: RecommenderLogsResponse | null): boolean => {
        return model?.submodel?.some(sm => sm.name === DEFAULT_SUBMODEL) || false;
    };

    const getAvailableCategories = (): TCategory[] => {
        if (!filteredCategories.length || !model?.submodel) return [];

        const currentSubmodelId = submodel?.submodel_id;
        const currentSubmodelCategories = new Set(submodel?.categories || []);

        const categoriesInOtherNonDefaultSubmodels = new Set<string>();

        model.submodel.forEach(sm => {
            // Skip current submodel and default submodel
            if (sm.submodel_id !== currentSubmodelId && sm.name !== DEFAULT_SUBMODEL && sm.categories?.length) {
                sm.categories.forEach(catId => {
                    categoriesInOtherNonDefaultSubmodels.add(catId);
                });
            }
        });

        return filteredCategories.filter(category =>
            !currentSubmodelCategories.has(category.id) &&
            !categoriesInOtherNonDefaultSubmodels.has(category.id)
        );
    };

    const selectAll = () => {
        const availableCategories = getAvailableCategories();

        if (availableCategories.length === 0) return;

        const categoriesToAdd = availableCategories.map(cat => cat.id);

        setModel(prevModel => {
            if (!prevModel) return null;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const currentSubmodelIndex = updatedSubmodels.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (currentSubmodelIndex === -1) return prevModel;

            const currentSubmodel = { ...updatedSubmodels[currentSubmodelIndex] };
            const isCurrentSubmodelDefault = currentSubmodel.name === DEFAULT_SUBMODEL;
            const modelHasDefaultSubmodel = hasDefaultSubmodel(updatedModel);

            // Add categories to current submodel
            const currentCategories = new Set(currentSubmodel.categories || []);
            categoriesToAdd.forEach(catId => currentCategories.add(catId));
            currentSubmodel.categories = Array.from(currentCategories);

            // Remove from default if applicable
            if (modelHasDefaultSubmodel && !isCurrentSubmodelDefault) {
                const defaultSubmodelIndex = updatedSubmodels.findIndex(sm => sm.name === DEFAULT_SUBMODEL);

                if (defaultSubmodelIndex !== -1) {
                    const defaultSubmodel = { ...updatedSubmodels[defaultSubmodelIndex] };
                    const defaultCategories = new Set(defaultSubmodel.categories || []);

                    categoriesToAdd.forEach(catId => defaultCategories.delete(catId));
                    defaultSubmodel.categories = Array.from(defaultCategories);
                    updatedSubmodels[defaultSubmodelIndex] = defaultSubmodel;
                }
            }

            updatedSubmodels[currentSubmodelIndex] = currentSubmodel;
            updatedModel.submodel = updatedSubmodels;
            return updatedModel;
        });
    };

    const filterCategories = (categoryTypeId: string) => {
        setSelectedCategoryTypeId(categoryTypeId);
    };

    const handleSearch = (value: string) => {
        setSearchString(value);
    }

    // Unified categories map that handles all category-related checks
    const categoriesMap = useMemo(() => {
        if (!model?.submodel) {
            return {
                inCurrentSubmodel: new Set<string>(),
                inOtherSubmodels: new Map<string, string[]>(), // Map of categoryId -> [submodelNames]
                isOverridden: new Map<string, boolean>()
            };
        }

        // Track categories in current submodel
        const inCurrentSubmodel = new Set(submodel?.categories || []);

        // Track categories in other submodels
        const inOtherSubmodels = new Map<string, string[]>();

        // Collect categories from other submodels with their submodel names
        model.submodel.forEach(sm => {
            if (sm.name !== DEFAULT_SUBMODEL && sm.submodel_id !== submodel?.submodel_id && sm.categories?.length) {
                sm.categories.forEach(catId => {
                    const submodels = inOtherSubmodels.get(catId) || [];
                    submodels.push(sm.name);
                    inOtherSubmodels.set(catId, submodels);
                });
            }
        });

        // Calculate overridden categories (in others but not in current)
        const isOverridden = new Map<string, boolean>();
        inOtherSubmodels.forEach((submodels, catId) => {
            isOverridden.set(catId, !inCurrentSubmodel.has(catId));
        });

        return { inCurrentSubmodel, inOtherSubmodels, isOverridden };
    }, [model?.submodel, submodel?.submodel_id, submodel?.categories]);

    // Get full category objects for selected categories
    const selectedCategories = useMemo(() => {
        if (!submodel?.categories?.length || !allCategories?.length) {
            return [];
        }

        const categoryMap = new Map(
            allCategories.map(category => [category.id, category])
        );

        return submodel.categories
            .map(id => categoryMap.get(id))
            .filter((category): category is TCategory => category !== undefined);
    }, [submodel?.categories, allCategories]);

    const filteredCategories = useMemo(() => {
        if (!allCategories?.length) return [];

        const searchLower = searchString.toLowerCase();
        const selectedCategoryIds = new Set(submodel?.categories || []);

        return allCategories.filter(category => {
            // Filter out categories already in the current submodel
            if (selectedCategoryIds.has(category.id)) {
                return false;
            }

            // Filter by category type if selected
            if (selectedCategoryTypeId && category.cattype !== selectedCategoryTypeId) {
                return false;
            }

            // Filter by search string
            if (searchString) {
                const name = category.name?.toLowerCase() || '';
                const description = category.description?.toLowerCase() || '';

                if (!name.includes(searchLower) && !description.includes(searchLower)) {
                    return false;
                }
            }

            // Filter to show only overridden categories if checkbox is checked
            if (showOnlyOverridden) {
                return categoriesMap.isOverridden.get(category.id) === true;
            }

            return true;
        });
    }, [
        allCategories,
        selectedCategoryTypeId,
        searchString,
        submodel?.categories,
        showOnlyOverridden,
        categoriesMap.isOverridden
    ]);

    // Check if a category is overridden (in other submodels but not current)
    const isCategoryOverridden = (categoryId: string): boolean => {
        return categoriesMap.isOverridden.get(categoryId) === true;
    };

    // Check if a category is in the current submodel
    const isCategoryInCurrentSubmodel = (categoryId: string): boolean => {
        return categoriesMap.inCurrentSubmodel.has(categoryId);
    };

    // Toggle the "show only overridden" checkbox
    const toggleShowOnlyOverridden = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyOverridden(e.target.checked);
        setSearchString("");
        setSelectedCategoryTypeId("");
    };

    // Helper function for add/remove category
    const updateModelWithCategoryOperation = (
        categoryId: string,
        operation: 'add' | 'remove'
    ) => {
        if (!model || !submodel) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];

            // Find current submodel
            const currentSubmodelIndex = updatedSubmodels.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (currentSubmodelIndex === -1) return prevModel;

            const currentSubmodel = { ...updatedSubmodels[currentSubmodelIndex] };
            const categoryArray = currentSubmodel.categories ? [...currentSubmodel.categories] : [];
            const isCurrentlySelected = categoryArray.includes(categoryId);
            const isCurrentSubmodelDefault = currentSubmodel.name === DEFAULT_SUBMODEL;
            const modelHasDefaultSubmodel = hasDefaultSubmodel(updatedModel);

            // Early returns for invalid operations
            if (operation === 'add' && isCurrentlySelected) return prevModel;
            if (operation === 'remove' && !isCurrentlySelected) return prevModel;

            // Update current submodel categories
            if (operation === 'add') {
                currentSubmodel.categories = [...categoryArray, categoryId];
            } else {
                currentSubmodel.categories = categoryArray.filter(id => id !== categoryId);
            }

            // Handle default submodel logic if applicable
            if (modelHasDefaultSubmodel && !isCurrentSubmodelDefault) {
                const defaultSubmodelIndex = updatedSubmodels.findIndex(sm => sm.name === DEFAULT_SUBMODEL);

                if (defaultSubmodelIndex !== -1) {
                    const defaultSubmodel = { ...updatedSubmodels[defaultSubmodelIndex] };
                    const defaultCategories = defaultSubmodel.categories ? [...defaultSubmodel.categories] : [];

                    if (operation === 'add') {
                        // Remove from default when adding to non-default
                        if (defaultCategories.includes(categoryId)) {
                            defaultSubmodel.categories = defaultCategories.filter(id => id !== categoryId);
                            updatedSubmodels[defaultSubmodelIndex] = defaultSubmodel;
                        }
                    } else {
                        // Add to default when removing from non-default
                        if (!defaultCategories.includes(categoryId)) {
                            defaultSubmodel.categories = [...defaultCategories, categoryId];
                            updatedSubmodels[defaultSubmodelIndex] = defaultSubmodel;
                        }
                    }
                }
            }

            updatedSubmodels[currentSubmodelIndex] = currentSubmodel;
            updatedModel.submodel = updatedSubmodels;
            return updatedModel;
        });
    };

    const addCategory = (categoryId: string) => {
        updateModelWithCategoryOperation(categoryId, 'add');
    };

    const removeCategory = (categoryId: string) => {
        updateModelWithCategoryOperation(categoryId, 'remove');
    };

    const clearSelection = () => {

        if (!model || !submodel?.categories?.length) return;

        setModel(prevModel => {
            if (!prevModel) return null;

            const updatedModel = { ...prevModel };
            const updatedSubmodels = [...updatedModel.submodel];
            const currentSubmodelIndex = updatedSubmodels.findIndex(
                sm => sm.submodel_id === submodel.submodel_id
            );

            if (currentSubmodelIndex === -1) return prevModel;

            const currentSubmodel = { ...updatedSubmodels[currentSubmodelIndex] };
            const categoriesToMove = [...(currentSubmodel.categories || [])];
            const isCurrentSubmodelDefault = currentSubmodel.name === DEFAULT_SUBMODEL;
            const modelHasDefaultSubmodel = hasDefaultSubmodel(updatedModel);

            // Clear current submodel
            currentSubmodel.categories = [];

            // Move categories back to default if applicable
            if (modelHasDefaultSubmodel && !isCurrentSubmodelDefault && categoriesToMove.length > 0) {
                const defaultSubmodelIndex = updatedSubmodels.findIndex(sm => sm.name === DEFAULT_SUBMODEL);

                if (defaultSubmodelIndex !== -1) {
                    const defaultSubmodel = { ...updatedSubmodels[defaultSubmodelIndex] };
                    const defaultCategories = new Set(defaultSubmodel.categories || []);

                    // Add all categories back to default (avoiding duplicates)
                    categoriesToMove.forEach(catId => defaultCategories.add(catId));
                    defaultSubmodel.categories = Array.from(defaultCategories);
                    updatedSubmodels[defaultSubmodelIndex] = defaultSubmodel;
                }
            }

            updatedSubmodels[currentSubmodelIndex] = currentSubmodel;
            updatedModel.submodel = updatedSubmodels;
            return updatedModel;
        });
    };

    return (
        <div className="tw-w-full tw-ps-2 tw-pt-2">
            <div>
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-4 tw-text-left tw-bg-white tw-rounded-t tw-border tw-border-[#c9cccf]">
                    <span className="tw-text-lg tw-font-semibold">{BASE_CATEGORIES}</span>
                    <div role="button" data-testid="components-basecategoryselector-div-1" className='tw-p-1 tw-cursor-pointer' onClick={() => { setExpanded((prev) => !prev) }}>{expanded ? <UpArrow /> : <PlainDownArrow />}</div>
                </div>
                {expanded ? <div className="tw-h-auto tw-bg-[#f4f5f7] tw-rounded-bl tw-rounded-br">
                    <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-[#c9cccf]">
                        <div className="tw-mb-3 tw-flex tw-justify-between tw-items-center">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <span className="tw-text-lg tw-font-medium">{SELECTED_CATEGORIES}</span>
                                <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-5 tw-w-5 tw-rounded-full tw-bg-neutral-500 tw-text-white tw-text-xs">
                                    {selectedCategories?.length ?? 0}
                                </span>
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                            {selectedCategories.map((item: TCategory) => (
                                <CategoryItem
                                    key={item?.id}
                                    item={item}
                                    isSelected={true}
                                    handleClick={removeCategory}
                                    disabledClick={false}
                                />
                            ))}

                            {selectedCategories.length > 0 && (
                                <div role="button" data-testid="components-basecategoryselector-div-2"
                                    className="tw-text-blue-800 tw-text-xs tw-cursor-pointer hover:tw-underline tw-ml-1"
                                    onClick={clearSelection}
                                >
                                    Clear All
                                </div>
                            )}
                        </div>
                    </div>


                    <div className="tw-p-4 tw-bg-white tw-border tw-border-[#c9cccf]">
                        <div className="tw-mb-3 tw-flex tw-justify-between tw-items-center">
                            <span className="tw-text-lg tw-font-medium">{SELECT_CATEGORIES}</span>
                            <div className="tw-flex tw-space-x-2 tw-items-center">
                                < div className="tw-flex tw-items-center tw-gap-2">
                                    <input data-testid="components-basecategoryselector-input-1"
                                        type="checkbox"
                                        id="showOverridden"
                                        className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-blue-600 focus:tw-ring-accent-hover"
                                        onChange={toggleShowOnlyOverridden}
                                        checked={showOnlyOverridden}
                                    />
                                    <label htmlFor="showOverridden" className="tw-justify-start tw-text-sm tw-text-slate-500">
                                        {SHOW_ONLY_OVERRIDDEN}
                                    </label>
                                    <OverlayIcon
                                        message={SHOW_ONLY_OVERRIDDEN_MESSAGE}
                                        placement="bottom"
                                    />
                                </div>
                                <select data-testid="components-basecategoryselector-select-2"
                                    className="tw-w-48 tw-max-w-48 tw-px-2 tw-h-8 tw-bg-gray-100 tw-rounded tw-border tw-border-[#c9c9cf] tw-text-ellipsis tw-overflow-hidden"
                                    onChange={(e) => filterCategories(e.target.value)}
                                    value={selectedCategoryTypeId}
                                >
                                    <option value="">Select category type</option>
                                    {allCategoryTypes?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="tw-relative">
                                    <input data-testid="components-basecategoryselector-input-3"
                                        type="text"
                                        className="tw-px-2 tw-h-8 tw-bg-white tw-rounded tw-border tw-border-[#c9c9cf]"
                                        placeholder="Search Category"
                                        value={searchString}
                                        onChange={(e) => handleSearch(e.target.value)}
                                    />
                                    <span
                                        className="tw-absolute tw-inset-y-0 tw-right-0 tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-justify-center tw-text-gray-400"
                                        style={{ pointerEvents: "none" }}
                                    >
                                        <SearchIcon />
                                    </span>
                                </div>
                                {filteredCategories && getAvailableCategories()?.length > 0 ? <div role="button" data-testid="components-basecategoryselector-div-3" className="tw-justify-start tw-pl-3 tw-text-blue-800 tw-text-xs tw-cursor-pointer" onClick={selectAll}>Select All</div> : null}
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap">
                            {filteredCategories && filteredCategories.length > 0 ? (
                                filteredCategories
                                    .slice(
                                        0,
                                        viewMoreCategories || searchString !== ""
                                            ? filteredCategories.length
                                            : Math.min(numberOfCategoriesToShow, filteredCategories.length)
                                    )
                                    .map((item: TCategory) => {
                                        return <CategoryItem key={item?.id} item={item} isSelected={isCategoryInCurrentSubmodel(item.id)} handleClick={addCategory} isOverridden={isCategoryOverridden(item.id)} disabledClick={isCategoryOverridden(item.id)} />
                                    })
                            ) : searchString !== "" ? (
                                <div className="tw-text-gray-500">
                                    {NO_CATEGORY_FOUND_SEARCH_MESSAGE}
                                </div>
                            ) : (
                                <div className="tw-text-gray-500">{NO_CATEGORIES_AVAILABLE_MESSAGE}</div>
                            )}

                            {searchString === "" &&
                                filteredCategories?.length > numberOfCategoriesToShow && (
                                    <button data-testid="components-basecategoryselector-button-4"
                                        onClick={() => setViewMoreCategories(!viewMoreCategories)}
                                        className="tw-text-blue-500 hover:tw-text-blue-700 tw-ml-2"
                                    >
                                        {viewMoreCategories ? VIEW_LESS : VIEW_MORE}
                                    </button>
                                )}
                        </div>
                    </div>
                </div> : null}
            </div>
        </div >
    );
}

export default BaseCategorySelector